import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Checkbox, Empty, Input, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { AnalyticsSort, MeasureCell, QueryResult } from '../../../types/survey-analytics.types';

const { Text } = Typography;

type RankingRow = QueryResult['rows'][number];

export interface RankingViewProps {
  result?: QueryResult | null;
  loading?: boolean;
  error?: Error | null;
  search?: string;
  sort?: AnalyticsSort;
  onSearchChange?: (value: string) => void;
  onSortChange?: (value: AnalyticsSort) => void;
  includeZero?: boolean;
  onIncludeZeroChange?: (value: boolean) => void;
  onNextPage?: (cursor: string) => void;
}

function cellValue(cell?: MeasureCell): string | number {
  if (!cell) return '—';
  if (cell.state !== 'VALUE') return '—';
  return cell.value;
}

function percentValue(cell?: MeasureCell): string {
  if (!cell || cell.state !== 'VALUE') return '—';
  return `${cell.value}%`;
}

function baseLabel(base?: MeasureCell): string {
  if (!base) return 'Base indisponível';
  if (base.state === 'VALUE') return `Base global: ${base.value}`;
  if (base.reasonCode === 'PRIVACY_RESTRICTED' || base.state === 'SUPPRESSED') return 'Base protegida';
  if (base.reasonCode === 'LEGACY_LIMITED') return 'Base indisponível na versão legada';
  return 'Base vazia';
}

export function RankingView({
  result,
  loading = false,
  error = null,
  search = '',
  sort = 'COUNT_DESC',
  includeZero = false,
  onSearchChange,
  onSortChange,
  onIncludeZeroChange,
  onNextPage,
}: RankingViewProps) {
  const [draftSearch, setDraftSearch] = useState(search);
  const lastIdentity = useRef<string | null>(null);

  useEffect(() => {
    setDraftSearch(search);
  }, [search]);

  useEffect(() => {
    if (!result) return;
    const identity = `${result.versionId}:${result.questionId}`;
    if (lastIdentity.current && lastIdentity.current !== identity && search) {
      onSearchChange?.('');
      setDraftSearch('');
    }
    lastIdentity.current = identity;
  }, [result?.versionId, result?.questionId, search, onSearchChange]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (draftSearch !== search) onSearchChange?.(draftSearch);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [draftSearch, search, onSearchChange]);

  const columns = useMemo<ColumnsType<RankingRow>>(() => [
    {
      title: '#',
      key: 'position',
      width: 56,
      render: (_value, _row, index) => index + 1,
    },
    {
      title: 'Opção',
      key: 'label',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.label}</Text>
          {row.displayQualifier && <Text type="secondary">{row.displayQualifier}</Text>}
        </Space>
      ),
    },
    {
      title: 'Votos',
      key: 'count',
      align: 'right',
      render: (_, row) => cellValue(row.cells[0]?.measures.COUNT),
    },
    {
      title: '%',
      key: 'percent',
      align: 'right',
      render: (_, row) => percentValue(row.cells[0]?.measures.PERCENT_WITHIN_GROUP),
    },
  ], []);

  if (error) {
    return <Alert type="error" message="Não foi possível carregar o ranking" description={error.message} />;
  }

  const base = result?.groups?.[0]?.base;
  const totalOptions = result?.page.totalOptions ?? null;
  const visibleRows = result?.rows.length ?? 0;

  return (
    <Card title="Ranking">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            <Input.Search
              aria-label="Buscar opção"
              placeholder="Buscar opção"
              value={draftSearch}
              onChange={(event) => setDraftSearch(event.target.value)}
              allowClear
              style={{ width: 260 }}
            />
            <Checkbox checked={includeZero} onChange={(event) => onIncludeZeroChange?.(event.target.checked)}>
              Incluir op??es sem votos
            </Checkbox>
            <Select
              aria-label="Ordenação"
              value={sort}
              onChange={(value) => onSortChange?.(value)}
              style={{ width: 180 }}
              options={[
                { value: 'COUNT_DESC', label: 'Mais votos' },
                { value: 'LABEL_ASC', label: 'Nome' },
              ]}
            />
          </Space>
          <Tag>{baseLabel(base)}</Tag>
        </Space>

        {result?.warnings?.map((warning) => (
          <Alert key={warning.code} type="warning" message={warning.message} showIcon />
        ))}

        {!loading && result && result.rows.length === 0 ? (
          <Empty description="Nenhuma opção encontrada" />
        ) : (
          <Table
            rowKey={(row) => row.optionKey ?? row.label}
            loading={loading}
            dataSource={result?.rows ?? []}
            columns={columns}
            pagination={false}
            size="small"
            scroll={{ x: 560 }}
          />
        )}

        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text type="secondary">
            {totalOptions === null ? `${visibleRows} opções` : `${visibleRows} de ${totalOptions} opções`}
          </Text>
          {result?.page.nextCursor && (
            <Button onClick={() => onNextPage?.(result.page.nextCursor as string)}>
              Carregar mais
            </Button>
          )}
        </Space>
      </Space>
    </Card>
  );
}

export default RankingView;

